/**
 * The librarian's recording-kind choices: pure, so testable without the
 * Firebase build constants (same split as `clients/shift-role-policy.ts`).
 *
 * OMON-232 (Ed, 2026-09-30): `incident` is the ABC chart, a structured log.
 * Each entry stores A – The Antecedent / B – The Behaviour / C – The
 * Consequence as `component[]`, plus a readable `note` summary (the hybrid,
 * §7 C). It is offered OFF PRODUCTION ONLY until the carer app that can
 * record it has shipped: a production fact switched to `incident` before
 * then would meet carer apps that don't know the kind (expand/contract).
 */
import { appEnvForDatabase } from "../env-gate";

export const INCIDENT_KIND = "incident";

const BASE_KINDS: [string, string][] = [
  ["attainment-5", "attainment-5 (1–5 support scale)"],
  ["rating", "rating / numeric"],
  ["tally", "tally / count"],
  ["boolean", "yes / no"],
  // OMON-204: diary — timestamped free-text entries through the day (a
  // running care log). A variation on tally: many entries per shift, but
  // each entry's payload is prose + a time, not a number.
  ["diary", "daily log / diary"],
];

/** Is the incident (ABC chart) kind offered in this environment? */
export function incidentKindAvailable(databaseId: string): boolean {
  return appEnvForDatabase(databaseId) !== "prod";
}

/**
 * The kinds a librarian may choose. A fact that ALREADY has a kind outside
 * the list (e.g. `incident` seen in production) still gets its own option,
 * so opening it never silently re-labels it as something else.
 */
export function recordingKindChoices(databaseId: string, current?: string): [string, string][] {
  const kinds = [...BASE_KINDS];
  if (incidentKindAvailable(databaseId) || current === INCIDENT_KIND) {
    kinds.push([INCIDENT_KIND, "incident log (ABC chart)"]);
  }
  if (current && !kinds.some(([k]) => k === current)) kinds.push([current, current]);
  return kinds;
}
